import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Flame } from 'lucide-react';
import FlammeOlympiqueCarousel from '@/components/FlammeOlympiqueCarousel';
import { cities } from '@/data/cities';
import { blogArticles } from '@/data/blog-articles';
import BlogCard from '@/components/blog/BlogCard';
import CTASection from '@/components/CTASection';
import NousTrouver from '@/components/NousTrouver';
import SEO, { breadcrumbSchema, faqSchema, localBusinessSchema } from '@/components/SEO';
import VilleHero from '@/components/ville/VilleHero';
import VilleStats from '@/components/ville/VilleStats';
import VilleGoogleRating from '@/components/ville/VilleGoogleRating';
import VilleIntro from '@/components/ville/VilleIntro';
import VilleServices from '@/components/ville/VilleServices';
import VillePrestations from '@/components/ville/VillePrestations';
import VilleMethodologie from '@/components/ville/VilleMethodologie';
import VilleAcces from '@/components/ville/VilleAcces';
import VilleTemoignages from '@/components/ville/VilleTemoignages';
import VilleFAQ from '@/components/ville/VilleFAQ';
import VilleVoisines from '@/components/ville/VilleVoisines';

const VilleDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const city = cities.find((c) => c.slug === slug);

  if (!city) return <Navigate to="/404" replace />;

  const otherCities = cities.filter((c) => c.slug !== slug);
  const recentArticles = [...blogArticles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 4);

  return (
    <>
      <SEO
        title={city.metaTitle}
        description={city.metaDescription}
        path={`/ville/${slug}`}
        keywords={[`agence événementielle ${city.name}`, `wedding planner ${city.name}`, `organisation événement ${city.name}`]}
        jsonLd={[
          breadcrumbSchema([
            { name: 'Accueil', path: '/' },
            { name: 'Villes', path: '/services' },
            { name: city.name, path: `/ville/${slug}` },
          ]),
          { ...localBusinessSchema(), areaServed: { '@type': 'City', name: city.name } },
          faqSchema(city.faq.map((f) => ({ question: f.q, answer: f.a }))),
        ]}
      />

      {/* 1. Hero */}
      <VilleHero city={city} />

      {/* 2. Chiffres clés */}
      <VilleStats city={city} />

      {/* 3. Note Google */}
      <VilleGoogleRating />

      {/* 4. Introduction locale */}
      <VilleIntro city={city} />

      {/* 5. Accès */}
      <VilleAcces city={city} />

      {/* 6. Services / Prestations */}
      <VilleServices city={city} />

      {/* 7. Prestations détaillées */}
      <VillePrestations city={city} />

      {/* 8. Méthodologie */}
      <VilleMethodologie city={city} />

      {/* 9. Témoignages */}
      <VilleTemoignages city={city} />

      {/* 10. FAQ */}
      <VilleFAQ city={city} />

      {/* Highlight Flamme Olympique — Nogent-sur-Seine */}
      {city.slug === 'nogent-sur-seine' && (
        <section className="px-6 lg:px-8 py-12 bg-background">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 to-transparent p-6">
              <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-primary mb-3">Événement marquant</p>
              <p className="font-heading font-bold text-foreground text-sm mb-3 inline-flex items-center gap-2"><Flame size={16} className="text-primary" strokeWidth={1.5} /> Cygnature Production anime le passage de la Flamme Olympique à Nogent-sur-Seine</p>
              <FlammeOlympiqueCarousel />
              <p className="text-xs text-muted-foreground leading-relaxed">
                En 2024, nous avons eu l'honneur d'animer le passage de la Flamme Olympique Paris 2024 avec un spectacle de cheerleaders.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* 11. Derniers articles */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Actualités</p>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Nos derniers articles</h2>
            </div>
            <Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary text-primary font-heading font-medium text-sm hover:bg-primary hover:text-primary-foreground transition-colors shrink-0">
              Voir tous les articles <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {recentArticles.map((article, i) => (
              <BlogCard key={article.slug} article={article} index={i} animate />
            ))}
          </div>
        </div>
      </section>

      {/* 12. Villes voisines */}
      <VilleVoisines city={city} otherCities={otherCities} />

      {/* 13. Nous trouver */}
      <NousTrouver />

      {/* 14. CTA */}
      <CTASection
        title={`Un projet ${city.prefix} ${city.name} ?`}
        subtitle={`Contactez-nous pour discuter de votre projet ${city.prefix} ${city.name}. Devis gratuit et accompagnement personnalisé.`}
      />

      {/* Bandeau SEO */}
      <section className="py-10 px-6 lg:px-8 bg-background border-t border-border/30">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            <span className="font-heading font-bold text-foreground">Cygnature Production {city.prefix} {city.name}</span> — {city.seoParagraph}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {[
              { label: 'Nos services', path: '/services' },
              { label: 'À propos', path: '/a-propos' },
              { label: 'FAQ', path: '/faq' },
              { label: 'Blog', path: '/blog' },
              { label: 'Contact & devis gratuit', path: '/contact' },
            ].map((link) => (
              <Link key={link.path} to={link.path} onClick={() => window.scrollTo(0, 0)} className="text-sm text-primary font-heading font-medium hover:underline inline-flex items-center gap-1">
                {link.label} <ArrowRight size={13} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default VilleDetail;
